export const PIECE_STAGES = ['draft', 'queued', 'published', 'archived'] as const;

export type PublicationKind = 'x_account' | 'website' | 'telegram';

export function publicationKindLabel(kind: PublicationKind): string {
  if (kind === 'x_account') return 'X account';
  if (kind === 'website') return 'Website magazine';
  return 'Telegram publication';
}

export function contentTypeLabel(format: string): string {
  if (format === 'tweet') return 'Tweets';
  if (format === 'article') return 'Articles';
  if (format === 'digest') return 'Digest';
  return format;
}

/** Keeps the UI readable during the production migration that adds `kind`. */
export function inferPublicationKind(publication: {
  kind?: PublicationKind;
  profiles?: { destination: string }[];
}): PublicationKind {
  if (publication.kind) return publication.kind;
  if (publication.profiles?.some((profile) => profile.destination === 'x')) return 'x_account';
  if (publication.profiles?.some((profile) => profile.destination === 'web')) return 'website';
  return 'telegram';
}

export type PieceStage = (typeof PIECE_STAGES)[number];

export interface PipelinePiece {
  id: number;
  editorialStage: PieceStage;
  title: string | null;
  bodyMd: string | null;
  scheduledFor: string | null;
  updatedAt: string;
}

export function piecesByStage<T extends PipelinePiece>(items: T[]): Record<PieceStage, T[]> {
  const grouped: Record<PieceStage, T[]> = {
    draft: [],
    queued: [],
    published: [],
    archived: [],
  };
  for (const item of items) grouped[item.editorialStage].push(item);
  for (const stage of PIECE_STAGES) {
    grouped[stage].sort((a, b) => {
      const aTime = Date.parse(a.scheduledFor ?? a.updatedAt);
      const bTime = Date.parse(b.scheduledFor ?? b.updatedAt);
      return aTime - bTime || a.id - b.id;
    });
  }
  return grouped;
}

export function pieceLabel(piece: Pick<PipelinePiece, 'id' | 'title' | 'bodyMd'>): string {
  const title = piece.title?.trim();
  if (title) return title;
  const body = piece.bodyMd?.replace(/\s+/g, ' ').trim();
  if (!body) return `Untitled piece #${piece.id}`;
  return body.length > 72 ? `${body.slice(0, 71)}…` : body;
}

export function isPieceReadOnly(piece: {
  editorialStage: string;
  deliveryStatus: string | null;
  needsReconciliation?: boolean;
}): boolean {
  return (
    piece.needsReconciliation === true ||
    piece.editorialStage === 'published' ||
    piece.deliveryStatus === 'publishing' ||
    piece.deliveryStatus === 'published'
  );
}

export type CadenceFrequency = 'none' | 'daily' | 'weekly' | 'custom';

export interface CadenceFields {
  frequency: CadenceFrequency;
  time: string;
  weekday: string;
}

export function parseCadenceCron(cron: string | null): CadenceFields {
  if (!cron) return { frequency: 'none', time: '09:00', weekday: '1' };
  const match = cron.match(/^(\d{1,2}) (\d{1,2}) \* \* (\*|[0-6])$/);
  if (!match) return { frequency: 'custom', time: '09:00', weekday: '1' };
  const [, minute, hour, day] = match;
  return {
    frequency: day === '*' ? 'daily' : 'weekly',
    time: `${hour!.padStart(2, '0')}:${minute!.padStart(2, '0')}`,
    weekday: day === '*' ? '1' : day!,
  };
}

export function buildCadenceCron(
  frequency: Exclude<CadenceFrequency, 'custom'>,
  time: string,
  weekday = '1',
): string | null {
  if (frequency === 'none') return null;
  const [hour = '9', minute = '0'] = time.split(':');
  return `${Number(minute)} ${Number(hour)} * * ${frequency === 'daily' ? '*' : weekday}`;
}

function timeLabel(hour: number, minute: number): string {
  const suffix = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${String(minute).padStart(2, '0')} ${suffix}`;
}

const WEEKDAYS = ['Sundays', 'Mondays', 'Tuesdays', 'Wednesdays', 'Thursdays', 'Fridays', 'Saturdays'];

export function cadenceLabel(cron: string | null, timezone: string): string {
  if (!cron) return 'No automatic cadence';
  const common = cron.match(/^(\d{1,2}) (\d{1,2}) \* \* (\*|[0-6])$/);
  if (common) {
    const minute = Number(common[1]);
    const hour = Number(common[2]);
    const when = common[3] === '*' ? 'Daily' : WEEKDAYS[Number(common[3])];
    return `${when} at ${timeLabel(hour, minute)} · ${timezone}`;
  }
  const interval = cron.match(/^(\d{1,2}) \*\/(\d{1,2}) \* \* \*$/);
  if (interval) return `Every ${Number(interval[2])} hours · ${timezone}`;
  return `Custom cadence · ${timezone}`;
}

export function parsePromptPreferences(value: string): string[] {
  return value
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
}

export function parseSettings(value: string): Record<string, unknown> {
  if (!value.trim()) return {};
  const parsed: unknown = JSON.parse(value);
  if (!parsed || Array.isArray(parsed) || typeof parsed !== 'object') {
    throw new Error('Settings must be a JSON object.');
  }
  return parsed as Record<string, unknown>;
}
